import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { api } from '@/lib/api';
import { useApiData } from '@/lib/useApi';
import type { LeadsTrendPoint, StudentStatusSlice } from '@/lib/types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const STATUS_COLORS: Record<string, string> = {
  lead: '#F59E0B', new: '#F59E0B', applied: '#3B82F6', visa: '#8B5CF6', converted: '#10B981',
};
const FALLBACK_COLORS = ['#F59E0B', '#3B82F6', '#8B5CF6', '#10B981', '#EF4444', '#06B6D4'];

function monthLabel(ym: string): string {
  const month = Number(ym.split('-')[1]);
  return MONTHS[month - 1] ?? ym;
}

export default function ChartsRow() {
  const { data: trend, loading: trendLoading } = useApiData<LeadsTrendPoint[]>(() => api.leadsTrend());
  const { data: status, loading: statusLoading } = useApiData<StudentStatusSlice[]>(() => api.studentStatus());

  const leadsChartData = (trend ?? []).map((point) => ({ month: monthLabel(point.ym), leads: Number(point.leads) }));
  const studentStatusData = (status ?? []).map((slice, index) => ({
    name: slice.name ? slice.name.charAt(0).toUpperCase() + slice.name.slice(1) : 'Unknown',
    value: Number(slice.value),
    color: STATUS_COLORS[(slice.name || '').toLowerCase()] ?? FALLBACK_COLORS[index % FALLBACK_COLORS.length],
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Bar Chart */}
      <div className="bg-white rounded-xl p-5 border border-[#E1E5EA]">
        <h3 className="text-sm font-semibold text-[#3C3C3C] mb-4">Leads Added — Last 6 Months</h3>
        {trendLoading ? (
          <div className="h-[220px] flex items-center justify-center text-sm text-gray-400">Loading…</div>
        ) : leadsChartData.length === 0 ? (
          <div className="h-[220px] flex items-center justify-center text-sm text-gray-400">No leads recorded yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={leadsChartData} barSize={28}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '12px' }} cursor={{ fill: '#f9fafb' }} />
              <Bar dataKey="leads" fill="#0B6E77" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Pie Chart */}
      <div className="bg-white rounded-xl p-5 border border-[#E1E5EA]">
        <h3 className="text-sm font-semibold text-[#3C3C3C] mb-4">Student Status Breakdown</h3>
        {statusLoading ? (
          <div className="h-[220px] flex items-center justify-center text-sm text-gray-400">Loading…</div>
        ) : studentStatusData.length === 0 ? (
          <div className="h-[220px] flex items-center justify-center text-sm text-gray-400">No students recorded yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={studentStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                {studentStatusData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', fontSize: '12px' }} />
              <Legend iconType="circle" iconSize={8} formatter={(value) => <span style={{ fontSize: '12px', color: '#6B7280' }}>{value}</span>} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
